import { useCallback, useState } from 'react';

import { useSearchOrderItems } from '@frontend/api';

import ExpandableFilterList from '../components/ExpandableFilterList';
import OrderItemMatchCell from '../components/OrderItemMatchCell';
import SearchResultList from '../components/SearchResultList';
import Searching from '../components/Searching';
import { TABS } from '../constants';
import { useSearchDataContext } from '../context';
import {
  buildFilterSeeMore,
  buildFilterUpdate,
} from '../utils/buildFilterUpdate';

const ALWAYS_VISIBLE_FIELDS = [
  'partNumber',
  'orderNumber',
  'partName',
  'revision',
  'process',
  'material',
  'finishes',
];

const OrderItemResultList = () => {
  const { userHasTyped, query, activeTab } = useSearchDataContext();

  const [paginateFrom, setPaginateFrom] = useState(
    query.orderItems.paginateFrom,
  );
  const [filters, setFilters] = useState(query.orderItems.filters);

  const { data: orderItems } = useSearchOrderItems(
    {
      ...query.orderItems,
      paginateFrom,
      filters,
    },
    {
      queryOptions: {
        keepPreviousData: true,
        enabled: activeTab === TABS.ORDER_ITEMS,
      },
    },
  );

  const onProcessChange = useCallback(
    (process) => buildFilterUpdate(setFilters, 'process')(process),
    [],
  );

  const onProcessSeeMore = useCallback(
    () => buildFilterSeeMore(setFilters, 'process')(),
    [],
  );

  const onMaterialChange = useCallback(
    (material) => buildFilterUpdate(setFilters, 'materials')(material),
    [],
  );

  const onMaterialSeeMore = useCallback(
    () => buildFilterSeeMore(setFilters, 'materials')(),
    [],
  );

  const onFinishChange = useCallback(
    (finish) => buildFilterUpdate(setFilters, 'finishes')(finish),
    [],
  );

  const onFinishSeeMore = useCallback(
    () => buildFilterSeeMore(setFilters, 'finishes')(),
    [],
  );

  if (userHasTyped) {
    return <Searching />;
  }

  return (
    <SearchResultList
      data={orderItems}
      aliases={{
        privateNotes: 'Internal Notes',
      }}
      tabKey={TABS.ORDER_ITEMS}
      labelPlural="Order Items"
      labelSingular="Order Item"
      matchCell={(result) => <OrderItemMatchCell orderItemResult={result} />}
      highlightConfig={{
        alwaysVisibleFields: ALWAYS_VISIBLE_FIELDS,
        children: {
          contact: [
            {
              field: 'contactFullName',
              key: 'contactFullName',
              label: 'Contact Name',
              showOnlyIfHighlighted: true,
            },
            {
              field: 'accountName',
              key: 'accountName',
              label: 'Account Name',
              showOnlyIfHighlighted: true,
            },
            {
              field: 'contactEmail',
              key: 'contactEmail',
              label: 'Contact Email',
              showOnlyIfHighlighted: true,
            },
          ],
        },
      }}
      filters={
        <>
          <ExpandableFilterList
            agg={orderItems?.aggs.processesAgg.processes}
            label="Process"
            selectedKeys={filters.process.values}
            onClick={onProcessChange}
            onSeeMore={onProcessSeeMore}
          />
          <ExpandableFilterList
            agg={orderItems?.aggs.materialsAgg.materials}
            label="Materials"
            selectedKeys={filters.materials.values}
            onClick={onMaterialChange}
            onSeeMore={onMaterialSeeMore}
          />
          <ExpandableFilterList
            agg={orderItems?.aggs.finishesAgg.finishes}
            label="Finishes"
            selectedKeys={filters.finishes.values}
            onClick={onFinishChange}
            onSeeMore={onFinishSeeMore}
          />
        </>
      }
      paginationInfo={{
        paginateFrom,
        paginateSize: query.orderItems.paginateSize,
      }}
      onPaginate={(pagination) => setPaginateFrom(pagination.paginateFrom)}
    />
  );
};

export default OrderItemResultList;
